import React, { useContext, useEffect, useState } from 'react'
import StepWizard, { StepWizardChildProps } from 'react-step-wizard'

import BenefitsSelection from './BenefitsSelection'
import NoBenefitsFoundStep from './NoBenefitsFoundStep'
import SSIBenefitsWizardContainer from './SSIBenefitsWizard'

import ACPWizardContainer from './ACP'
import CalFreshWizardContainer from './CalFresh'
import CalWorksWizardContainer from './CalWorks'
import HUDHousingWizardContainer from './HUDHousing'
import HeadStartWizardContainer from './HeadStart'
import ThankYouPage from './ThankYouPage'

import { useSelectedBenefitsQuery } from '../../../types/graphql'

import SBox from 'components/opinionless/SBox'
import { useRouter } from 'next/router'
import { SStyled } from 'styles/theme'
import { ApplicationContext } from 'v2/context/application'
import BenefitsIntroduction from './BenefitsIntroduction'

interface Props {
    step?: number
    person?: any
    stepName?: any
    backwardsStep?: boolean
    previousStep?: (number) => void
    goToNextStep?: (number) => void
    updatePercentage?: any
    fromBackButton?: boolean
}

const benefitComponents = {
    SSI: SSIBenefitsWizardContainer,
    HUD_HOUSING: HUDHousingWizardContainer,
    CAL_FRESH: CalFreshWizardContainer,
    CAL_WORKS: CalWorksWizardContainer,
    HEAD_START: HeadStartWizardContainer,
    AFFORDABLE_CONNECTIVITY: ACPWizardContainer,
    NONE: NoBenefitsFoundStep,
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
    margin-bottom: 20px;
`

const BenefitsWizardContainer: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const applicationContext = useContext(ApplicationContext)
    const [selectedBenefits, setSelectedBenefits] = useState([...applicationContext.benefits])

    const { data: selectedBenefitsResponse, loading: loadingSelectedBenefits } = useSelectedBenefitsQuery({
        variables: { personId: applicationContext.profile?.person?.id },
        fetchPolicy: 'network-only',
    })

    const router = useRouter()
    const [applyingHeadStartThisYear, setApplyingHeadStartThisYear] = useState<boolean>(false)
    const [acpOptionOne, setAcpOptionOne] = useState<boolean>(false)
    const [backWardsStep, setIsBackwardsStep] = useState<boolean>(false)
    const [currentStep, setCurrentStep] = useState<number>(props.fromBackButton ? 2 + selectedBenefits.length : 1)

    useEffect(() => {
        if (applicationContext.application.id) {
            router.replace(
                `${
                    window.PERSON?.applicationDetails[0].programIAL_USER?.program?.urlFragment ||
                    window.PROGRAM.urlFragment
                }/apply/${applicationContext.application.id}/benefits`
            )
        }
    }, [])

    const updateSelectedBenefits = (benefits) => {
        if (benefits.includes('NONE')) {
            applicationContext.setBenefits([])
            goToNextStep()
            return
        }

        setSelectedBenefits(benefits)
        applicationContext.setBenefits(benefits)
    }

    useEffect(() => {
        if (selectedBenefitsResponse) {
            const selectedBenefits = selectedBenefitsResponse.selectedBenefits.map((b) => b.category)
            setSelectedBenefits(selectedBenefits)
            applicationContext.setBenefits(selectedBenefits)
        }
    }, [selectedBenefitsResponse])

    useEffect(() => {
        window.localStorage.removeItem('EDITING_BENEFITS_INFORMATION')
    }, [])

    const goToNextStep = () => {
        props.goToNextStep(props.step + 1)
    }

    const parentFlowPrevious = props.previousStep

    return (
        <Container>
            <StepWizard
                transitions={{
                    enterRight: '',
                    enterLeft: '',
                    exitRight: '',
                    exitLeft: '',
                }}
                initialStep={currentStep}
                isLazyMount={true}
                onStepChange={(stepInfo: { previousStep: number; activeStep: number }) => {
                    stepInfo.activeStep < stepInfo.previousStep ? setIsBackwardsStep(true) : setIsBackwardsStep(false)
                }}
            >
                <BenefitsIntroduction
                    usedForReview
                    personId={applicationContext.profile?.person?.id}
                    onPrevious={parentFlowPrevious}
                    benefitsFlow
                    acpOptionOne={acpOptionOne}
                    updatePercentage={props.updatePercentage}
                />

                <BenefitsSelection
                    setIsBackwardsStep={setIsBackwardsStep}
                    selectedBenefits={selectedBenefits}
                    personId={applicationContext.profile?.person?.id}
                    onAnswer={updateSelectedBenefits}
                    updatePercentage={props.updatePercentage}
                />

                {selectedBenefits.includes('SSI') && (
                    <SSIBenefitsWizardContainer
                        key={'SSI'}
                        personId={applicationContext.profile?.person?.id}
                        onNextStep={props.nextStep}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                {selectedBenefits.includes('HUD_HOUSING') && (
                    <HUDHousingWizardContainer
                        key={'HUD_HOUSING'}
                        personId={applicationContext.profile?.person?.id}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                {selectedBenefits.includes('CAL_FRESH') && (
                    <CalFreshWizardContainer
                        key={'CAL_FRESH'}
                        personId={applicationContext.profile?.person?.id}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                {selectedBenefits.includes('CAL_WORKS') && (
                    <CalWorksWizardContainer
                        key={'CAL_WORKS'}
                        personId={applicationContext.profile?.person?.id}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                {selectedBenefits.includes('HEAD_START') && (
                    <HeadStartWizardContainer
                        key={'HEAD_START'}
                        personId={applicationContext.profile?.person?.id}
                        onApplyHeadStart={(value) => setApplyingHeadStartThisYear(value)}
                        onFinish={props.nextStep}
                        selectedBenefits={selectedBenefits}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                {selectedBenefits.includes('AFFORDABLE_CONNECTIVITY') && (
                    <ACPWizardContainer
                        key={'AFFORDABLE_CONNECTIVITY'}
                        backwardsStep={backWardsStep}
                        personId={applicationContext.profile?.person?.id}
                        onAcpOptionOne={(value) => setAcpOptionOne(value)}
                        onFinish={() => goToNextStep()}
                        updatePercentage={props.updatePercentage}
                    />
                )}

                <ThankYouPage onFinish={goToNextStep} />
            </StepWizard>
        </Container>
    )
}

export default BenefitsWizardContainer
